import { Button, MenuItem, Stack, styled, TextField } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { SpotClosedOrdersParams } from 'api/v1/spot'
import Iconfont from 'components/Iconfont'
import useWindow from 'hooks/useWindow'
import { FC, memo, useEffect, useMemo, useState } from 'react'
import CsvDownloader from 'react-csv-downloader'
import { useMarketTrades } from 'store/market/hooks'
import { Flex } from 'styles/Common'
import { darkColor } from '../../styles/constants/dark'
import { FilterSelect, FormItemHeight } from './Select'
import { PERP_ENABLED } from 'config'
import dayjs from 'dayjs'
import { ExSelect } from 'components/ExSelect'
import { ExSelectItem } from 'components/ExSelect/ExSelectItem'

export interface FilterValues extends SpotClosedOrdersParams {
  marketType: number
}

const ExportButton = styled(Button)`
  color: ${(props) => props.theme.color?.primary30};
  border: 1px solid ${(props) => props.theme.color?.primary30};
  font-weight: 600;
  height: ${FormItemHeight};
  border-radius: 6px;
  &:hover {
    background: ${(props) => props.theme.color?.primary30};
    color: ${(props) => props.theme.color?.text00};
  }
  ${(props) => props.theme.breakpoints.down('md')} {
    flex: 0 0 100px;
    padding: 0;
  }
`

const DatePickerIconWrap = styled(Flex)`
  color: ${(props) => props.theme.color?.primary30};
`

const DesktopDatePickerWrap = styled(DesktopDatePicker)`
  width: 130px;
  height: ${FormItemHeight};
`
const MobileDatePickerWrap = styled(MobileDatePicker)``

const DatePickerIcon: FC = memo(() => {
  return (
    <DatePickerIconWrap>
      <Iconfont name="icon-date" size={20}></Iconfont>
    </DatePickerIconWrap>
  )
})
const pickerStyle = {
  '& .MuiOutlinedInput-root.MuiInputBase-formControl:hover .MuiOutlinedInput-notchedOutline':
    {
      borderColor: (props: any) => props.color.primary20,
      // background: (props: any) => props.color.primary20,
    },
  '& .MuiDayPicker-monthContainer .MuiButtonBase-root.Mui-selected': {
    background: (props: any) => props.color.primary20,
    color: darkColor.text00,
  },
  '& .MuiDayPicker-monthContainer .MuiButtonBase-root.Mui-selected:hover': {
    background: (props: any) => props.color.primary30,
  },
  '& .MuiButtonBase-root.MuiPickersDay-dayWithMargin': {
    background: 'unset',
  },
  '& button.MuiButtonBase-root.MuiPickersDay-today': {
    borderColor: (props: any) => props.color.primary30,
  },
  '& .MuiTextField-root .MuiOutlinedInput-root.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline':
    {
      borderColor: (props: any) => props.color.DatePickerInput,
    },
  '& .MuiTextField-root .MuiOutlinedInput-root.MuiInputBase-formControl:hover .MuiOutlinedInput-notchedOutline':
    {
      borderColor: (props: any) => props.color.primary30,
    },
  '& .MuiFormControl-root  .MuiFormLabel-root.Mui-focused': {
    color: (props: any) => props.color.primary30,
  },
}

export enum MarketType {
  Spot,
  Perp,
}

export const OrderFilter: FC<{
  onChange?(values: FilterValues): void
  csvData?: any[]
  defaultValue: FilterValues
}> = memo(({ onChange, csvData = [], defaultValue }) => {
  const { isMobile } = useWindow()
  const market = useMarketTrades()

  const [values, setValues] = useState<FilterValues>(defaultValue)

  const currencies = useMemo(() => {
    if (values.marketType === MarketType.Spot) {
      return market.spotsSymbols
    } else {
      return market.contractsSymbols
    }
  }, [values.marketType])

  const handleChange = (key: string, newValue: any) => {
    if (key === 'range' && newValue) {
      newValue = newValue.format('YYYYMM')
    }
    if (key === 'marketType') {
      setValues((vs) => ({ ...vs, [key]: newValue, symbol: '' }))
    } else {
      setValues((vs) => ({ ...vs, [key]: newValue }))
    }
  }

  useEffect(() => {
    onChange && onChange(values)
  }, [values])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {isMobile ? (
        <>
          <Stack
            spacing={1}
            direction="row"
            padding="0 20px"
            marginBottom="8px"
            justifyContent="space-between"
            width="100%"
          >
            <FilterSelect
              label="Market"
              value={values.marketType}
              onChange={(val) => {
                handleChange('marketType', val)
              }}
            >
              <MenuItem sx={{ fontSize: 14 }} value={MarketType.Spot}>
                Spot
              </MenuItem>
              {PERP_ENABLED && (
                <MenuItem sx={{ fontSize: 14 }} value={MarketType.Perp}>
                  Perp
                </MenuItem>
              )}
            </FilterSelect>
            <FilterSelect
              label="Symbol"
              clear
              value={values.symbol}
              onChange={(val) => {
                handleChange('symbol', val)
              }}
            >
              {currencies.map((item) => (
                <MenuItem sx={{ fontSize: 14 }} key={item.id} value={item.name}>
                  {item.name}
                </MenuItem>
              ))}
            </FilterSelect>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            padding="0 20px"
            marginBottom="8px"
            justifyContent="space-between"
            width="100%"
          >
            <MobileDatePickerWrap
              sx={{
                ...pickerStyle,
                height: FormItemHeight,
                flex: 1,
                '.MuiInputBase-input': {
                  height: FormItemHeight,
                },
              }}
              label="Date"
              format="MM/YYYY"
              value={dayjs(values.range)}
              views={['year', 'month']}
              disableFuture
              onChange={(newValue) => handleChange('startTime', newValue)}
              slotProps={{
                textField: { variant: 'outlined' },
              }}
            />
            <CsvDownloader
              filename={`order-history-${values.range}`}
              extension=".csv"
              datas={csvData}
              style={{
                flex: 1,
              }}
            >
              <ExportButton variant="outlined" fullWidth>
                Export CSV
              </ExportButton>
            </CsvDownloader>
          </Stack>
        </>
      ) : (
        <Stack
          direction="row"
          spacing={1}
          padding={isMobile ? '0 20px' : '0'}
          marginBottom={isMobile ? '8px' : '8px'}
          justifyContent="space-between"
          flexWrap="wrap"
          width="100%"
        >
          <Stack spacing={1} flexWrap="wrap" direction="row">
            {/* {PERP_ENABLED && (
              <FilterSelect
                label="Market"
                value={values.marketType}
                onChange={(val) => {
                  handleChange('marketType', val)
                }}
              >
                <MenuItem sx={{ fontSize: 14 }} value={MarketType.Spot}>
                  Spot
                </MenuItem>
                <MenuItem sx={{ fontSize: 14 }} value={MarketType.Perp}>
                  Perp
                </MenuItem>
              </FilterSelect>
            )} */}
            {PERP_ENABLED && (
              <ExSelect
                label={'Market'}
                value={values.marketType}
                onChange={(val) => {
                  handleChange('marketType', val)
                }}
              >
                <ExSelectItem value={MarketType.Spot}>Spot</ExSelectItem>
                <ExSelectItem value={MarketType.Perp}>Perp</ExSelectItem>
              </ExSelect>
            )}

            <ExSelect
              label={'Symbol'}
              value={values.symbol}
              onChange={(val) => {
                handleChange('symbol', val)
              }}
            >
              {/* <ExSelectItem value={MarketType.Spot}>Spot</ExSelectItem> */}
              {/* <ExSelectItem value={MarketType.Perp}>Perp</ExSelectItem> */}
              <ExSelectItem value={'all'}>All</ExSelectItem>
              {currencies.map((item) => {
                return (
                  <ExSelectItem key={item.id} value={item.name}>
                    {item.name}
                  </ExSelectItem>
                )
              })}
            </ExSelect>

            {/* <FilterSelect
              label="Symbol"
              clear
              value={values.symbol}
              onChange={(val) => {
                handleChange('symbol', val)
              }}
            >
              {currencies.map((item) => (
                <MenuItem sx={{ fontSize: 14 }} key={item.id} value={item.name}>
                  {item.name}
                </MenuItem>
              ))}
            </FilterSelect> */}
            <DesktopDatePickerWrap
              sx={pickerStyle}
              label="Date"
              format="MM/YYYY"
              value={dayjs(values.range)}
              views={['year', 'month']}
              disableFuture
              onChange={(val) => {
                handleChange('range', val)
              }}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  sx: {
                    '.MuiInputLabel-root': {
                      lineHeight: FormItemHeight,
                      fontSize: '12px',
                      transform: `translate(14px, 0px) scale(1)`,
                      pointerEvents: 'none',
                      '&.Mui-focused': {
                        transform: `translate(14px, -12px) scale(1)`,
                      },
                      '&.MuiFormLabel-filled': {
                        transform: `translate(14px, -12px) scale(1)`,
                      },
                    },
                    '.MuiInputBase-input': {
                      height: FormItemHeight,
                      paddingTop: 0,
                      paddingBottom: 0,
                      // lineHeight: FormItemHeight,
                      fontSize: '12px',
                    },
                  },
                },
              }}
              slots={{
                openPickerIcon: DatePickerIcon,
              }}
            />
          </Stack>
          <CsvDownloader
            filename={`order-history-${values.range}`}
            extension=".csv"
            datas={csvData}
          >
            <ExportButton variant="outlined">Export CSV</ExportButton>
          </CsvDownloader>
        </Stack>
      )}
    </LocalizationProvider>
  )
})
